import type { AssetRegistry } from "../assets/AssetRegistry";
import type { AssetManifestAsset } from "../assets/AssetManifest";
import { wallClockSleep } from "../boot/utils/wallClockSleep";
import { BOOT_REQUEST_TIMEOUT_MS } from "../boot/retry/BootRequestTimeout";
import { BrandedAssetResolver } from "./BrandedAssetResolver";
import { validateOperatorManifest, type OperatorManifest } from "./OperatorManifest";
import type { Log } from "@slot/presentation";

export interface OperatorBrandingServiceDeps {
  assets: AssetRegistry;
  sharedBundle: string;
  /** Base URL the manifest resolves under: `{base}/operators/{id}/manifest.json`. */
  manifestBaseUrl: string;
  gameVersion?: string;
  log: Log;
  maxRetryAttempts?: number;
  retryDelaysMs?: number[];
}

export interface OperatorBrandingResolution {
  resolver: BrandedAssetResolver;
  operatorId: string | null;
  manifest: OperatorManifest | null;
  operatorBundle: string | null;
  /** Null when the operator phase completed successfully or was skipped intentionally. */
  failureReason: string | null;
}

/**
 * The operator branding phase:
 *   1. fetch `{base}/operators/{id}/manifest.json` (bounded retry, never throws),
 *   2. validate schema + version gate,
 *   3. load the operator bundle: `manifest.bundleUrl` is either a bundle NAME in the built manifest
 *      (the in-repo sample `pilot-brand`) or a URL to a remote bundle descriptor
 *      (`{ name?, assets: [{ alias, src, data? }], files? }`, registered via `AssetRegistry.addBundle`),
 *   4. build a `BrandedAssetResolver`.
 * Any failure short-circuits to the default brand.
 */
export class OperatorBrandingService {
  private readonly deps: OperatorBrandingServiceDeps;

  public constructor(deps: OperatorBrandingServiceDeps) {
    this.deps = deps;
  }

  /**
   * `preloaded`: the manifest the early loader branding already fetched for this operator (the boot
   * passes it only when the ids match), so the full phase skips its own fetch.
   */
  public async resolve(operatorId: string | null, preloaded?: { manifest: OperatorManifest }): Promise<OperatorBrandingResolution> {
    const unbranded = (id: string | null, manifest: OperatorManifest | null, failureReason: string | null): OperatorBrandingResolution => ({
      resolver: this.buildResolver(null, new Set(), id),
      operatorId: id,
      manifest,
      operatorBundle: null,
      failureReason,
    });
    if (!operatorId) return unbranded(null, null, null);

    let manifest: OperatorManifest;
    if (preloaded) {
      manifest = preloaded.manifest;
      this.deps.log(`[Branding] operator="${operatorId}" manifest reused from the early loader branding`);
    } else {
      const manifestResult = await this.fetchManifestSafe(operatorId);
      if (manifestResult.ok === false) {
        this.deps.log(`[Branding] skipping operator="${operatorId}" — ${manifestResult.reason}`);
        return unbranded(operatorId, null, manifestResult.reason);
      }
      manifest = manifestResult.manifest;
    }

    const versionReason = this.checkVersionCompatibility(manifest);
    if (versionReason) {
      this.deps.log(`[Branding] skipping operator="${operatorId}" — ${versionReason}`);
      return unbranded(operatorId, manifest, versionReason);
    }

    const bundleResult = await this.loadOperatorBundleSafe(manifest);
    if (bundleResult.ok === false) {
      this.deps.log(`[Branding] skipping operator="${operatorId}" — ${bundleResult.reason}`);
      return unbranded(operatorId, manifest, bundleResult.reason);
    }

    const overrides = new Set(manifest.assetOverrides);
    this.deps.log(`[Branding] operator="${operatorId}" version=${manifest.version} bundle=${bundleResult.bundle} overrides=${overrides.size}`);
    return {
      resolver: this.buildResolver(bundleResult.bundle, overrides, operatorId),
      operatorId,
      manifest,
      operatorBundle: bundleResult.bundle,
      failureReason: null,
    };
  }

  private buildResolver(operatorBundle: string | null, assetOverrides: ReadonlySet<string>, operatorId: string | null): BrandedAssetResolver {
    return new BrandedAssetResolver({
      assets: this.deps.assets,
      sharedBundle: this.deps.sharedBundle,
      operatorBundle,
      assetOverrides,
      operatorId,
      log: this.deps.log,
    });
  }

  private async fetchManifestSafe(operatorId: string): Promise<{ ok: true; manifest: OperatorManifest } | { ok: false; reason: string }> {
    const base = this.deps.manifestBaseUrl.replace(/\/+$/, "");
    const url = `${base}/operators/${encodeURIComponent(operatorId)}/manifest.json`;
    const fetchResult = await this.boundedRetry(`operator_manifest[${operatorId}]`, async () => {
      const res = await fetch(url, { method: "GET", signal: AbortSignal.timeout(BOOT_REQUEST_TIMEOUT_MS) });
      if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);
      return (await res.json()) as unknown;
    });
    if (fetchResult.ok === false) return { ok: false, reason: `manifest fetch failed: ${fetchResult.error}` };
    const validation = validateOperatorManifest(fetchResult.value, operatorId);
    if (validation.ok === false) return { ok: false, reason: `manifest invalid: ${validation.reason}` };
    return { ok: true, manifest: validation.manifest };
  }

  private async loadOperatorBundleSafe(manifest: OperatorManifest): Promise<{ ok: true; bundle: string } | { ok: false; reason: string }> {
    const ref = manifest.bundleUrl;
    const result = await this.boundedRetry(`operator_bundle[${manifest.operatorId}]`, () => loadBundleByRef(this.deps.assets, ref, `operator-${manifest.operatorId}`));
    if (result.ok === false) return { ok: false, reason: `bundle load failed: ${result.error}` };
    return { ok: true, bundle: result.value };
  }

  private checkVersionCompatibility(manifest: OperatorManifest): string | null {
    const required = manifest.minCompatibleGameVersion;
    if (!required) return null;
    const current = this.deps.gameVersion;
    if (!current) return `manifest requires minCompatibleGameVersion=${required} but client version is unknown`;
    if (compareSemver(current, required) < 0) return `client version ${current} is below manifest.minCompatibleGameVersion=${required}`;
    return null;
  }

  private async boundedRetry<T>(taskName: string, run: () => Promise<T>): Promise<{ ok: true; value: T } | { ok: false; error: string }> {
    const maxAttempts = this.deps.maxRetryAttempts ?? 3;
    const delays = this.deps.retryDelaysMs ?? [300, 900, 2100];
    let lastError = "unknown";
    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      try {
        return { ok: true, value: await run() };
      } catch (error) {
        lastError = error instanceof Error ? error.message : String(error);
        this.deps.log(`[Branding] ${taskName} attempt=${attempt}/${maxAttempts} failed: ${lastError}`);
        if (attempt < maxAttempts) await wallClockSleep(delays[Math.min(attempt - 1, delays.length - 1)]!);
      }
    }
    return { ok: false, error: lastError };
  }
}

/**
 * Loads the bundle a manifest names: a bundle of the assets manifest by name, else a remote bundle
 * descriptor URL (`{ name?, assets: [{ alias, src, data? }], files? }`, aliases prefixed `<name>:` by the
 * descriptor; an entry is the built manifest's `AssetManifestAsset`, so its `data.trim` frames an image
 * as in a built bundle) registered under its name (or `fallbackName`). Resolves to the loaded bundle name.
 */
export async function loadBundleByRef(assets: AssetRegistry, ref: string, fallbackName: string): Promise<string> {
  if (assets.hasBundle(ref)) {
    await assets.loadBundle(ref);
    return ref;
  }
  const res = await fetch(ref, { method: "GET", signal: AbortSignal.timeout(BOOT_REQUEST_TIMEOUT_MS) });
  if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);
  const descriptor = (await res.json()) as { name?: string; assets?: AssetManifestAsset[]; files?: Record<string, string> };
  const name = descriptor.name ?? fallbackName;
  if (!Array.isArray(descriptor.assets)) throw new Error("bundle descriptor has no assets array");
  assets.addBundle(name, descriptor.assets, descriptor.files ?? {});
  await assets.loadBundle(name);
  return name;
}

/** Naive semver compare on `major.minor.patch`; non-numeric parts degrade to string compare. */
export function compareSemver(a: string, b: string): number {
  const pa = a.split(".");
  const pb = b.split(".");
  const length = Math.max(pa.length, pb.length);
  for (let i = 0; i < length; i += 1) {
    const sa = pa[i] ?? "0";
    const sb = pb[i] ?? "0";
    const na = Number(sa);
    const nb = Number(sb);
    if (Number.isFinite(na) && Number.isFinite(nb)) {
      if (na !== nb) return na - nb;
    } else {
      const cmp = sa.localeCompare(sb);
      if (cmp !== 0) return cmp;
    }
  }
  return 0;
}
